///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import { requestRender } from "@stovokor/engine/RenderRequest"
import { RenderState } from "@stovokor/engine/RenderState"
import type { GestureHitResult, AnyGestureRecognizer } from "@stovokor/gestures/GestureRecognizer"
import type { StovokorContext } from "@stovokor/StovokorContext"
import { GestureArena, effectiveGesturePriority } from "./GestureArena"
import { TouchPoint } from "./GestureRecognizer"
import { ZoomGestureRecognizer } from "./ZoomGestureRecognizer"
import { type FocusTarget } from "./FocusTarget"
import { type GestureTarget } from "./GestureNode"
import { HoverGestureRecognizer } from "./HoverGestureRecognizer"

/**
 * GestureSystem - Unified gesture handling and focus management
 * *
 * ## Event Flow
 *
 * **Touch gestures (pan, tap, pinch, etc.)**:
 * 1. DOM event arrives
 * 2. Hit test scene to find GestureNodes under touch points
 * 3. Collect recognizers from: hit nodes + viewport handler
 * 4. Create arena, let recognizers compete
 * 5. Winner's callback fires
 *
 * **Wheel events (zoom)**:
 * 1. DOM wheel event arrives
 * 2. Route through focused node > default focus > viewport handler's ZoomGestureRecognizer
 */
export class GestureSystem {
	private context: StovokorContext
	private boundingRect: DOMRect

	private activeTouches = new Map<number, TouchPoint>()
	private activeArena?: GestureArena
	// Latest hover move not yet hit-tested, see flushHover
	private pendingHover?: PointerEvent

	// Track currently hovered recognizers for proper enter/leave events
	private hoverRecognizers = new Set<HoverGestureRecognizer>()

	private pointerDownHandler: (e: PointerEvent) => void
	private pointerMoveHandler: (e: PointerEvent) => void
	private pointerUpHandler: (e: PointerEvent) => void
	private pointerCancelHandler: (e: PointerEvent) => void
	private pointerEnterHandler: (e: PointerEvent) => void
	private pointerLeaveHandler: (e: PointerEvent) => void
	private wheelHandler: (e: WheelEvent) => void
	private resizeHandler: () => void
	private resizeObserver?: ResizeObserver
	private keyModifierHandler: (e: KeyboardEvent) => void
	private blurHandler: () => void

	// Tracks whether a modifier key (ctrl/shift/alt/meta) is physically held.
	// Used to distinguish a real ctrl+scroll from a macOS trackpad pinch, which
	// synthesizes ctrlKey on the wheel event without any physical key pressed.
	private physicalModifierHeld = false

	constructor(context: StovokorContext) {
		this.context = context
		this.boundingRect = context.canvas.getBoundingClientRect()

		// Bind handlers
		this.pointerDownHandler = this.onPointerDown.bind(this)
		this.pointerMoveHandler = this.onPointerMove.bind(this)
		this.pointerUpHandler = this.onPointerUp.bind(this)
		this.pointerCancelHandler = this.onPointerCancel.bind(this)
		this.pointerEnterHandler = this.onPointerEnter.bind(this)
		this.pointerLeaveHandler = this.onPointerLeave.bind(this)
		this.wheelHandler = this.onWheel.bind(this)
		this.resizeHandler = this.updateBoundingRect.bind(this)
		this.keyModifierHandler = this.onKeyModifier.bind(this)
		this.blurHandler = this.onBlur.bind(this)

		const canvas = this.context.canvas

		// Prevent browser default touch gestures (scroll, pinch-to-zoom, etc.)
		// so all touch input flows through pointer events exclusively
		canvas.style.touchAction = "none"

		canvas.addEventListener("pointerdown", this.pointerDownHandler)
		canvas.addEventListener("pointermove", this.pointerMoveHandler)
		canvas.addEventListener("pointerup", this.pointerUpHandler)
		canvas.addEventListener("pointercancel", this.pointerCancelHandler)
		canvas.addEventListener("pointerenter", this.pointerEnterHandler)
		canvas.addEventListener("pointerleave", this.pointerLeaveHandler)
		canvas.addEventListener("wheel", this.wheelHandler, { passive: false })
		window.addEventListener("resize", this.resizeHandler)

		// Also watch the canvas itself - it can resize without a window resize
		// (layout shifts, container changes, mobile URL bar collapse)
		if (typeof ResizeObserver !== "undefined") {
			this.resizeObserver = new ResizeObserver(this.resizeHandler)
			this.resizeObserver.observe(canvas)
		}
		window.addEventListener("keydown", this.keyModifierHandler)
		window.addEventListener("keyup", this.keyModifierHandler)
		window.addEventListener("blur", this.blurHandler)
	}

	/**
	 * Detach event listeners and clean up
	 */
	destroy(): void {
		const canvas = this.context.canvas
		canvas.removeEventListener("pointerdown", this.pointerDownHandler)
		canvas.removeEventListener("pointermove", this.pointerMoveHandler)
		canvas.removeEventListener("pointerup", this.pointerUpHandler)
		canvas.removeEventListener("pointercancel", this.pointerCancelHandler)
		canvas.removeEventListener("pointerenter", this.pointerEnterHandler)
		canvas.removeEventListener("pointerleave", this.pointerLeaveHandler)
		canvas.removeEventListener("wheel", this.wheelHandler)
		window.removeEventListener("resize", this.resizeHandler)
		this.resizeObserver?.disconnect()
		this.resizeObserver = undefined
		window.removeEventListener("keydown", this.keyModifierHandler)
		window.removeEventListener("keyup", this.keyModifierHandler)
		window.removeEventListener("blur", this.blurHandler)

		this.activeArena?.reset()
		this.activeArena = undefined
		this.activeTouches.clear()
		this.pendingHover = undefined
	}

	/**
	 * Update the cached bounding rect
	 */
	private updateBoundingRect(): void {
		this.boundingRect = this.context.canvas.getBoundingClientRect()
		this.context.canvasResized = true
	}

	/**
	 * Reset all gesture state
	 */
	reset(): void {
		this.activeArena?.reset()
		this.activeArena = undefined
		this.activeTouches.clear()
		this.hoverRecognizers.clear()
		this.pendingHover = undefined
	}

	// ========== Pointer Event Handlers ==========

	private onPointerDown(e: PointerEvent): void {
		requestRender()
		// Refresh the cached rect at gesture start - the canvas may have moved
		// without firing a resize (page scroll, layout shift). One rect query
		// per gesture start is cheap and keeps hit-test coordinates correct.
		this.updateBoundingRect()

		const touch = this.pointerEventToTouch(e)
		this.activeTouches.set(e.pointerId, touch)

		// Capture pointer to receive move/up events even if pointer leaves canvas
		this.context.canvas.setPointerCapture(e.pointerId)

		if(!this.activeArena) {
			this.activeArena = new GestureArena(touch.gestureRecognizers)
		} else {
			this.activeArena.addRecognizers(touch.gestureRecognizers)
		}

		this.activeArena.touchStart([touch])

		if (this.shouldPreventDefault()) {
			e.preventDefault()
		}
	}

	private onPointerMove(e: PointerEvent): void {
		requestRender()
		// A pointer we saw go down on the canvas is always a drag, regardless of
		// e.buttons - some WebKit versions report buttons === 0 for touch/pen
		// moves, which would otherwise route active drags to the hover path
		const isHovering = !this.activeTouches.has(e.pointerId) && e.buttons === 0

		if (isHovering) {
			// Hover needs a full scene hit test; pointer events can arrive
			// several times per frame, so only the latest position is kept
			// and resolved once per tick in flushHover()
			this.pendingHover = e
		} else {
			this.pendingHover = undefined
			// Touch/drag events go through the arena. Feed every coalesced
			// sample, not just the one the browser dispatched: at 120 Hz, or
			// whenever a frame runs long, the dispatched event is only the
			// last of several, and velocity / twist estimates want them all
			const coalesced = typeof e.getCoalescedEvents === "function" ? e.getCoalescedEvents() : []
			const events = coalesced.length > 0 ? coalesced : [e]
			for (const event of events) {
				const touch = this.pointerEventToTouch(event, false)

				if(!this.activeArena) {
					this.activeArena = new GestureArena(touch.gestureRecognizers)
				} else {
					this.activeArena.addRecognizers(touch.gestureRecognizers)
				}
				this.activeArena.touchMove([touch])
			}
		}

		if (this.shouldPreventDefault()) {
			e.preventDefault()
		}
	}

	/**
	 * Resolve the most recent hover position against the scene. Called by
	 * the engine loop once per tick; hover events bypass the arena and call
	 * the hover recognizers directly
	 */
	public flushHover(): void {
		const e = this.pendingHover
		if (e === undefined) {
			return
		}
		this.pendingHover = undefined
		if (this.activeTouches.has(e.pointerId)) {
			return		// a press started since; the arena owns this pointer now
		}

		const touch = this.pointerEventToTouch(e, true)
		const currentHoverRecognizers = this.resolveHoverRecognizers(touch)

		// Call handlePointerLeave on recognizers that are no longer hovered
		for (const recognizer of this.hoverRecognizers) {
			if (!currentHoverRecognizers.has(recognizer)) {
				recognizer.handlePointerLeave()
			}
		}

		// Call handlePointerEnter on newly hovered recognizers
		for (const recognizer of currentHoverRecognizers) {
			if (!this.hoverRecognizers.has(recognizer)) {
				recognizer.handlePointerEnter(touch)
			}
		}

		// Call handlePointerMove on currently hovered recognizers
		for (const recognizer of currentHoverRecognizers) {
			recognizer.handlePointerMove(touch)
		}

		// Update tracked hover recognizers
		this.hoverRecognizers = currentHoverRecognizers
	}

	/**
	 * Pick the hover recognizers that own the pointer this tick. Hover has
	 * no arena, so without arbitration every hit node's recognizer would
	 * fire and whichever ran last would own shared UI like the hover card
	 * (the farthest hit, the opposite of what a tap selects). Instead the
	 * same effective priority the arena uses (base priority plus proximity
	 * boost) ranks the hovered nodes, and only the winning node's hover
	 * recognizers fire, so hover and tap land on the same object
	 */
	private resolveHoverRecognizers(touch: TouchPoint): Set<HoverGestureRecognizer> {
		const hoverRecognizers = touch.gestureRecognizers.filter(
			(r): r is HoverGestureRecognizer => r instanceof HoverGestureRecognizer
		)
		if (hoverRecognizers.length <= 1) {
			return new Set(hoverRecognizers)
		}

		// gestureHits is sorted closest-first, so the first hit per node is its nearest
		const hitDistance = (r: HoverGestureRecognizer): number =>
			touch.gestureHits.find(h => h.node === r.owner)?.distance ?? Infinity

		let winner = hoverRecognizers[0]
		let winnerPriority = -Infinity
		for (const r of hoverRecognizers) {
			const priority = effectiveGesturePriority(r.priority, hitDistance(r))
			if (priority > winnerPriority) {
				winner = r
				winnerPriority = priority
			}
		}

		// A node with several hover recognizers keeps all of them
		return new Set(hoverRecognizers.filter(r =>
			r === winner || (winner.owner !== undefined && r.owner === winner.owner)
		))
	}

	private onPointerUp(e: PointerEvent): void {
		requestRender()
		// Release pointer capture
		try {
			this.context.canvas.releasePointerCapture(e.pointerId)
		} catch {
			// Pointer may already be released?
		}

		const touch = this.pointerEventToTouch(e)
		this.activeArena?.touchEnd([touch])


		// Clean up arena if all touches ended
		this.activeTouches.delete(e.pointerId)
		if (this.activeTouches.size === 0) {
			this.activeArena = undefined
		}
	}

	private onPointerCancel(e: PointerEvent): void {
		requestRender()
		// Release pointer capture
		try {
			this.context.canvas.releasePointerCapture(e.pointerId)
		} catch {
			// Pointer may already be released
		}

		this.activeTouches.delete(e.pointerId)
		if (this.activeTouches.size === 0) {
			this.activeArena?.touchCancel()
			this.activeArena = undefined
		}
	}

	private onPointerEnter(_e: PointerEvent): void {
		// Pointer entered canvas - nothing special needed
	}

	private onPointerLeave(e: PointerEvent): void {
		requestRender()
		this.pendingHover = undefined
		// Call handlePointerLeave on all currently hovered recognizers
		for (const recognizer of this.hoverRecognizers) {
			recognizer.handlePointerLeave()
		}
		this.hoverRecognizers.clear()

		// Cancel any active pointer gestures
		this.activeTouches.delete(e.pointerId)
		if (this.activeTouches.size === 0) {
			this.activeArena?.touchCancel()
			this.activeArena = undefined
		}
	}

	// ========== Wheel Event Handler ==========

	/**
	 * Handle wheel events by routing through zoom recognizers
	 * Priority: focused node > default focus > viewport handler
	 */
	private onWheel(e: WheelEvent): void {
		requestRender()
		// Try zoom recognizer in priority order: focusedNode → defaultFocus → viewportHandler
		const manager = this.context.focusManager
		const focusedNode = manager.getFocusedNode() as (FocusTarget & GestureTarget) | undefined
		const focusedZoom = focusedNode?.recognizers.find(r => r.gestureType === "zoom") as ZoomGestureRecognizer | undefined
		const defaultNode = manager.getDefaultFocus() as (FocusTarget & GestureTarget) | undefined
		const defaultZoom = defaultNode?.recognizers.find(r => r.gestureType === "zoom") as ZoomGestureRecognizer | undefined
		const viewportNode = manager.getViewportHandler() as (FocusTarget & GestureTarget) | undefined
		const viewportZoom = viewportNode?.recognizers.find(r => r.gestureType === "zoom") as ZoomGestureRecognizer | undefined

		let zoomRecognizer: ZoomGestureRecognizer | undefined
		if(focusedNode?.focusable && focusedZoom?.enabled) {
			zoomRecognizer = focusedZoom
		} else if(defaultNode?.focusable && defaultZoom?.enabled) {
			zoomRecognizer = defaultZoom
		} else if(viewportNode?.focusable && viewportZoom?.enabled) {
			zoomRecognizer = viewportZoom
		}

		if(zoomRecognizer) {
			const canvasX = e.clientX - this.boundingRect.left
			const canvasY = e.clientY - this.boundingRect.top
			zoomRecognizer.handleWheelEvent(e, canvasX, canvasY, this.physicalModifierHeld)
		}

		// Prevent default if zoom recognizer is active
		if (zoomRecognizer?.isActive()) {
			e.preventDefault()
		}
	}

	// ========== Keyboard Modifier Tracking ==========

	/**
	 * Track whether a modifier key is physically held. Reading the modifier flags
	 * off the keyboard event reflects real key state (unlike the synthetic ctrlKey
	 * macOS sets on trackpad-pinch wheel events).
	 */
	private onKeyModifier(e: KeyboardEvent): void {
		this.physicalModifierHeld = e.ctrlKey || e.shiftKey || e.altKey || e.metaKey
	}

	private onBlur(): void {
		// Key-up may be missed when the window loses focus; clear stale state.
		this.physicalModifierHeld = false
	}

	// ========== Helpers ==========

	private shouldPreventDefault(): boolean {
		return (this.activeArena?.getActiveRecognizers().length ?? 0) > 0
	}

	private pointerEventToTouch(e: PointerEvent, hoverOnly = false): TouchPoint {
		// The event's own time (performance.now() timeline): coalesced samples
		// arrive in a burst but carry their real spacing
		const now = e.timeStamp > 0 ? e.timeStamp : performance.now()
		const existing = this.activeTouches.get(e.pointerId)

		const x = e.clientX - this.boundingRect.left
		const y = e.clientY - this.boundingRect.top
		const startX = existing?.startX ?? x
		const startY = existing?.startY ?? y

		const newTouch = new TouchPoint(e.pointerId, x, y, startX, startY, now, {
			ctrlKey: e.ctrlKey,
			shiftKey: e.shiftKey,
			altKey: e.altKey,
			metaKey: e.metaKey
		}, e.pointerType || "mouse")

		// Preserve previously recognized touch data
		if(existing) {
			newTouch.gestureHits = existing.gestureHits
			newTouch.gestureRecognizers = existing.gestureRecognizers
		} else {
			this.setGestureNodesForTouch(newTouch, hoverOnly)
		}

		return newTouch
	}

	private setGestureNodesForTouch(touch: TouchPoint, hoverOnly = false): void {
		const allHits: GestureHitResult[] = []

		// Check UI viewport first (2D panes have priority over 3D world)
		const uiViewportHandler = this.context.focusManager.getUIViewportHandler()
		if (uiViewportHandler) {
			const uiHits = uiViewportHandler.gestureHitTest2D(touch.x, touch.y)
			allHits.push(...uiHits)
		}

		// Check label viewport (depth-preserving labels between world and UI)
		const labelViewportHandler = this.context.focusManager.getLabelViewportHandler()
		if (labelViewportHandler) {
			const labelHits = labelViewportHandler.gestureHitTest2D(touch.x, touch.y)
			allHits.push(...labelHits)
		}

		// Then check 3D world viewport
		const viewportHandler = this.context.focusManager.getViewportHandler()
		if (viewportHandler) {
			const camera = viewportHandler.camera
			const state = new RenderState(camera, touch.x, touch.y)
			const worldHits = viewportHandler.gestureHitTest(state)
			allHits.push(...worldHits)
		}

		// Sort all hits by distance (closest first)
		const closestHits = allHits.sort((a: GestureHitResult, b: GestureHitResult) => a.distance - b.distance)
		touch.gestureHits = closestHits

		// Collect recognizers from hit nodes
		let allRecognizers = closestHits.flatMap(hit => hit.node.recognizers)

		// If no 3D world hits, fall back to default focus (e.g., Earth) or viewport handler
		// This allows dragging in empty space (stars) to still rotate the globe
		if (closestHits.length === 0) {
			const manager = this.context.focusManager
			const defaultNode = manager.getDefaultFocus() as (FocusTarget & GestureTarget) | undefined
			const viewportNode = manager.getViewportHandler() as (FocusTarget & GestureTarget) | undefined

			if (defaultNode?.recognizers) {
				allRecognizers = defaultNode.recognizers
			} else if (viewportNode?.recognizers) {
				allRecognizers = viewportNode.recognizers
			}
		}

		// Filter by hover-only if no button pressed
		touch.gestureRecognizers = hoverOnly
			? allRecognizers.filter(r => r.gestureType === "hover")
			: allRecognizers.filter(r => r.gestureType !== "hover")
	}
}
